/**
 * Session Keepalive Hook
 *
 * Maintains session activity with the Django backend and handles idle timeout.
 * Mirrors the AngularJS idleTimeout module behavior.
 */

import { useEffect, useRef, useCallback } from 'react';

// Time constants
const SECONDS = 1000;
const MINUTES = 60 * SECONDS;

// Configuration - matches AngularJS angular-common.js
const DEFAULT_LOGOUT_TIME = 15 * MINUTES;
const DEFAULT_WARN_TIME = 2 * MINUTES;
const EXTENDED_LOGOUT_TIME = 60 * MINUTES;
const EXTENDED_WARN_TIME = 5 * MINUTES;
const TICK_INTERVAL = 1 * SECONDS;
const HEARTBEAT_INTERVAL = 4 * MINUTES;

/**
 * Get cookie value by name
 */
function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

/**
 * Get CSRF token from cookie
 */
function getCSRFToken(): string {
  return getCookie('csrftoken') || '';
}

interface UseSessionKeepaliveOptions {
  /** Disable the warning dialog before logout */
  disableWarning?: boolean;
  /** Custom warning callback instead of default alert */
  onWarning?: (remainingTime: number) => void;
  /** Custom logout callback */
  onLogout?: () => void;
  /** Enable/disable the hook */
  enabled?: boolean;
}

/**
 * Hook to maintain session keepalive and handle idle timeout
 */
export function useSessionKeepalive(options: UseSessionKeepaliveOptions = {}) {
  const {
    disableWarning = false,
    onWarning,
    onLogout,
    enabled = true
  } = options;

  const timeToLogoutRef = useRef<number>(0);
  const warnedRef = useRef<boolean>(false);
  const disableWarningRef = useRef<boolean>(disableWarning);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const tickIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Track the warning preference in a ref rather than reading `disableWarning` inside the main
  // effect. It is a dependency of that effect, so toggling it would tear the effect down and
  // rebuild it - resetting the idle countdown and firing an extra heartbeat every time the user
  // saves their preferences. The ref lets the change take effect on the next tick instead.
  useEffect(() => {
    disableWarningRef.current = disableWarning;
  }, [disableWarning]);

  // Determine timeout values based on rememberMe cookie
  const isRememberMe = getCookie('rememberMe') === 'true';
  const LOGOUT_TIME = isRememberMe ? EXTENDED_LOGOUT_TIME : DEFAULT_LOGOUT_TIME;
  const WARN_TIME = isRememberMe ? EXTENDED_WARN_TIME : DEFAULT_WARN_TIME;

  /**
   * Reset the idle timer on user activity
   */
  const resetTimer = useCallback(() => {
    timeToLogoutRef.current = LOGOUT_TIME;
    warnedRef.current = false;
  }, [LOGOUT_TIME]);

  /**
   * Send heartbeat to server to keep session alive
   */
  const sendHeartbeat = useCallback(async () => {
    try {
      await fetch('/auth/keepalive', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
        },
      });
    } catch (error) {
      console.warn('Session keepalive failed:', error);
    }
  }, []);

  /**
   * Handle logout - redirect to timeout page
   */
  const handleLogout = useCallback(() => {
    // Clear any local storage
    try {
      localStorage.clear();
    } catch (e) {
      // Ignore localStorage errors
    }

    if (onLogout) {
      onLogout();
    } else {
      window.location.replace('/auth/timeout');
    }
  }, [onLogout]);

  /**
   * Show warning to user
   */
  const showWarning = useCallback((remainingTime: number) => {
    if (onWarning) {
      onWarning(remainingTime);
    } else {
      // Default warning using browser confirm
      const remainingSeconds = Math.ceil(remainingTime / 1000);
      const confirmed = window.confirm(
        `You will be signed out in ${remainingSeconds} seconds due to inactivity.\n\nClick OK to stay signed in.`
      );
      if (confirmed) {
        resetTimer();
      }
    }
  }, [onWarning, resetTimer]);

  useEffect(() => {
    if (!enabled) return;

    // Initialize timer
    timeToLogoutRef.current = LOGOUT_TIME;

    // Set up activity listeners
    const handleActivity = () => {
      resetTimer();
    };

    document.addEventListener('keypress', handleActivity);
    document.addEventListener('mousemove', handleActivity);
    document.addEventListener('click', handleActivity);
    document.addEventListener('scroll', handleActivity);
    document.addEventListener('touchstart', handleActivity);

    // Set up heartbeat interval (every 4 minutes)
    heartbeatIntervalRef.current = setInterval(() => {
      sendHeartbeat();
    }, HEARTBEAT_INTERVAL);

    // Send initial heartbeat
    sendHeartbeat();

    // Set up tick interval to check timeout
    tickIntervalRef.current = setInterval(() => {
      timeToLogoutRef.current -= TICK_INTERVAL;

      // Check for warning
      if (!disableWarningRef.current && !warnedRef.current && timeToLogoutRef.current <= WARN_TIME) {
        warnedRef.current = true;
        showWarning(timeToLogoutRef.current);
      }

      // Check for logout
      if (timeToLogoutRef.current <= 0) {
        handleLogout();
      }
    }, TICK_INTERVAL);

    // Cleanup
    return () => {
      document.removeEventListener('keypress', handleActivity);
      document.removeEventListener('mousemove', handleActivity);
      document.removeEventListener('click', handleActivity);
      document.removeEventListener('scroll', handleActivity);
      document.removeEventListener('touchstart', handleActivity);

      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current);
      }
    };
  }, [enabled, LOGOUT_TIME, WARN_TIME, resetTimer, sendHeartbeat, showWarning, handleLogout]);

  return {
    resetTimer,
    sendHeartbeat,
    isRememberMe,
    logoutTime: LOGOUT_TIME,
    warnTime: WARN_TIME,
  };
}

export default useSessionKeepalive;
